import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

type HoverEffect = 'orbit' | 'waves';

const Card = styled.div<{ $effect?: HoverEffect }>`
  @property --orbit-angle {
    syntax: '<angle>';
    initial-value: 0deg;
    inherits: false;
  }

  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  overflow: ${({ $effect }) => ($effect === 'waves' ? 'visible' : 'hidden')};
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
  }

  ${({ $effect }) => $effect === 'orbit' && `
    &::before,
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      padding: 2px;
      -webkit-mask:
        linear-gradient(#000 0 0) content-box,
        linear-gradient(#000 0 0);
      -webkit-mask-composite: xor;
      mask:
        linear-gradient(#000 0 0) content-box,
        linear-gradient(#000 0 0);
      mask-composite: exclude;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.25s ease;
    }

    &::before {
      background: conic-gradient(
        from 0deg,
        #5BCEFA 0deg,
        #5BCEFA 35deg,
        #F5A9B8 55deg,
        #FFFFFF 90deg,
        #F5A9B8 125deg,
        #5BCEFA 145deg,
        #5BCEFA 215deg,
        #F5A9B8 235deg,
        #FFFFFF 270deg,
        #F5A9B8 305deg,
        #5BCEFA 325deg,
        #5BCEFA 360deg
      );
      z-index: 1;
    }

    &::after {
      background: conic-gradient(
        from var(--orbit-angle),
        transparent 0deg,
        transparent 4deg,
        var(--card-bg) 18deg,
        var(--card-bg) 360deg
      );
      z-index: 2;
      animation: orbit 3s linear infinite;
      animation-play-state: paused;
    }

    &:hover::before,
    &:hover::after {
      opacity: 1;
    }

    &:hover::after {
      animation-play-state: running;
    }

    @keyframes orbit {
      to { --orbit-angle: 360deg; }
    }

    @media (prefers-reduced-motion: reduce) {
      &::after {
        animation: none;
      }
    }
  `}

  ${({ $effect }) => $effect === 'waves' && `
    &::before,
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      border: 1px solid var(--text);
      box-sizing: border-box;
      pointer-events: none;
      opacity: 0;
      transform: scale(1);
      transform-origin: center;
      z-index: -1;
    }

    &:hover {
      transform: translateY(-3px);
      animation: bob 0.9s ease-in-out 0.2s infinite;
    }

    &:hover::before {
      animation: wave 1.8s ease-out infinite;
    }

    &:hover::after {
      animation: wave 1.8s ease-out infinite;
      animation-delay: 0.9s;
    }

    @keyframes bob {
      0%, 100% { transform: translateY(-3px); }
      50%      { transform: translateY(-5px); }
    }

    @keyframes wave {
      0%   { transform: scale(1.01); opacity: 0.35; }
      100% { transform: scale(1.07); opacity: 0;    }
    }

    @media (prefers-reduced-motion: reduce) {
      &:hover {
        animation: none;
      }
      &:hover::before,
      &:hover::after {
        animation: none;
      }
    }
  `}
`;

const CardImage = styled.img`
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  display: block;
  border-radius: 10px 10px 0 0;
`;

const CardBody = styled.div`
  padding: 16px 18px 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-family: 'JetBrains Mono', monospace;
  font-weight: 700;
  color: var(--text);
`;

const CardDesc = styled.p`
  margin: 0;
  font-size: 15px;
  font-family: 'JetBrains Mono', monospace;
  font-weight: 400;
  color: var(--text);
  opacity: 0.8;
  line-height: 1.6;
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
`;

const Tag = styled.span`
  font-size: 13px;
  font-family: 'JetBrains Mono', monospace;
  font-weight: 500;
  color: var(--text);
  opacity: 0.7;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  border: 1px solid var(--card-border);
  border-radius: 4px;
  padding: 2px 7px;
`;

export interface ProjectCardProps {
  id: string;
  title: string;
  description: string;
  tags: string[];
  image?: string;
  imageStyle?: React.CSSProperties;
  hoverEffect?: HoverEffect;
  href?: string;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ id, title, description, tags, image, imageStyle, hoverEffect, href }) => {
  const navigate = useNavigate();
  const handleClick = () => {
    if (href) {
      window.location.href = href;
    } else {
      navigate(`/projects/${id}`);
    }
  };
  return (
    <Card $effect={hoverEffect} onClick={handleClick}>
      <CardImage src={image || '/projects/default-card-img.jpg'} alt={title} style={imageStyle} />
      <CardBody>
        <CardTitle>{title}</CardTitle>
        <CardDesc>{description}</CardDesc>
        <TagRow>
          {tags.map(tag => <Tag key={tag}>{tag}</Tag>)}
        </TagRow>
      </CardBody>
    </Card>
  );
};

export default ProjectCard;
